import React, { useContext } from "react";
import CompletionTask from "../ui/CompletionTask";
import ProfileCard from "../profile/ProfileCard";
import { FileTextIcon, CameraIcon, ClockIcon, VideoIcon } from "../common/IconsComponent";
import { useUser } from "../../contexts/UserContext";
import { DocumentsContext } from "../../contexts/DocumentsContext";
import { useAvailability } from "../../contexts/AvailabilityContext";

// ─── Constants ────────────────────────────────────────────────────────────────

const ICON_CLS = "text-blue-500";

// ─── Component ────────────────────────────────────────────────────────────────

const Sidebar = ({ scrollToSection }) => {
  const { user }               = useUser();
  const { documents, documentTypes } = useContext(DocumentsContext);
  const { savedAvailability }  = useAvailability();

  const role = user?.role || "student";
  const isTeacher = role === "teacher";
  const isSupplier = role === "supplier";

  // A supplier's "personal info" is just the business name (see
  // PersonalInfoSection.jsx) — they never have firstName/lastName set
  // at all, so checking those here would show this as permanently
  // incomplete for every supplier, even a fully-filled-in one.
  const isPersonalCompleted = isSupplier
    ? Boolean(
        user?.businessName && user?.contactPerson &&
        user?.email && Number(user?.emailConfirmed) === 1 &&
        user?.phone && Number(user?.phoneConfirmed) === 1
      )
    : Boolean(
        user?.firstName && user?.lastName &&
        user?.email     && Number(user?.emailConfirmed) === 1 &&
        user?.phone     && Number(user?.phoneConfirmed) === 1 &&
        (user?.aboutMe || "").length >= 20
      );

  // documentTypes is already the correct per-role list — the
  // DocumentsProvider itself computes this from effectiveRole
  // (TEACHER_DOCUMENT_TYPES / SUPPLIER_DOCUMENT_TYPES), so there's no
  // need to re-derive it here.
  const isDocumentsCompleted = documentTypes.every((type) =>
    (Array.isArray(documents) ? documents : []).some((d) => d?.type === type)
  );

  const isAvailabilityCompleted =
    Array.isArray(savedAvailability) &&
    savedAvailability.some((day) => Array.isArray(day) && day.some(Boolean));

  const isZoomCompleted = Boolean(
    user?.zoomAccountId && user?.zoomClientId && user?.zoomSecretKey
  );

  return (
    <div className="space-y-4">
      <ProfileCard scrollToSection={scrollToSection} />

      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 sm:p-5" dir="rtl">
        <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-4">
          להשלמת הפרופיל
        </h3>

        <div className="space-y-3">
          <CompletionTask
            title="פרטים אישיים"
            icon={<FileTextIcon size={16} className={ICON_CLS} />}
            completed={isPersonalCompleted}
          />

          {(isTeacher || isSupplier) && (
            <CompletionTask
              title="העלאת מסמכים"
              icon={<FileTextIcon size={16} className={ICON_CLS} />}
              completed={isDocumentsCompleted}
            />
          )}

          {isTeacher && (
            <>
              <CompletionTask
                title="הגדרת זמינות"
                icon={<ClockIcon size={16} className={ICON_CLS} />}
                completed={isAvailabilityCompleted}
              />
              <CompletionTask
                title="הגדרות זום"
                icon={<VideoIcon size={16} className={ICON_CLS} />}
                completed={isZoomCompleted}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;